// formatWhatsAppText — formatação leve estilo WhatsApp para o texto das mensagens.
//
// Converte marcadores inline em elementos React (NUNCA dangerouslySetInnerHTML —
// o texto é do cliente/IA e não pode injetar HTML):
//   *bold*  e  **bold**  → <strong>
//   _italic_             → <em>
//   ~strike~             → <s>
//   telefone BR solto     → `renderPhone` (opcional; a bolha põe as ações do número)
//
// Regras (deliberadamente conservadoras):
//   - o marcador abre e fecha na MESMA linha (não atravessa \n) — por isso o
//     parsing é feito linha a linha e as quebras são preservadas como '\n'
//     (o CSS do .text usa white-space: pre-wrap, então o \n vira quebra visual);
//   - marcador solto/desemparelhado (sem par na mesma linha) fica literal;
//   - conteúdo vazio (`**`, `__`) fica literal;
//   - sem aninhamento: o conteúdo interno de um marcador é renderizado como texto
//     puro (ex.: `*_x_*` mostra "_x_" em negrito). Um nível basta para o uso real;
//     aninhar negrito+itálico está fora do escopo.
import { Fragment, type ReactNode } from 'react';
import { findPhones, type PhoneMatch } from './phone';

/** Renderiza um telefone achado no texto puro (ex.: botão com as ações do número). */
export type PhoneRenderer = (match: PhoneMatch, key: string) => ReactNode;

interface MarkerDef {
  token: string;
  render: (inner: string, key: number) => ReactNode;
}

// Ordem importa: '**' precisa ser testado antes de '*'.
const MARKERS: MarkerDef[] = [
  { token: '**', render: (inner, key) => <strong key={key}>{inner}</strong> },
  { token: '*', render: (inner, key) => <strong key={key}>{inner}</strong> },
  { token: '_', render: (inner, key) => <em key={key}>{inner}</em> },
  { token: '~', render: (inner, key) => <s key={key}>{inner}</s> },
];

function markerAt(line: string, i: number): MarkerDef | null {
  for (const m of MARKERS) {
    if (line.startsWith(m.token, i)) return m;
  }
  return null;
}

/** Formata uma única linha (sem \n) em nós de texto + elementos de formatação. */
function parseLine(line: string, renderPhone?: PhoneRenderer): ReactNode[] {
  const out: ReactNode[] = [];
  let buffer = '';
  let key = 0;
  let i = 0;

  const flush = () => {
    if (!buffer) return;
    // Telefone só no texto puro (fora de marcador): dentro de *negrito* fica literal.
    const phones = renderPhone ? findPhones(buffer) : [];
    let last = 0;
    for (const m of phones) {
      if (m.start > last) out.push(buffer.slice(last, m.start));
      out.push(renderPhone!(m, `p${key++}`));
      last = m.end;
    }
    if (last < buffer.length) out.push(buffer.slice(last));
    buffer = '';
  };

  while (i < line.length) {
    const marker = markerAt(line, i);
    if (marker) {
      const contentStart = i + marker.token.length;
      const closeIdx = line.indexOf(marker.token, contentStart);
      // Par encontrado na mesma linha e conteúdo não-vazio → aplica formatação.
      if (closeIdx > contentStart) {
        flush();
        out.push(marker.render(line.slice(contentStart, closeIdx), key++));
        i = closeIdx + marker.token.length;
        continue;
      }
    }
    // Sem marcador válido aqui: acumula o caractere como literal.
    buffer += line[i];
    i += 1;
  }
  flush();
  return out;
}

/**
 * Formata `text` preservando quebras de linha. Retorna um ReactNode pronto para
 * ser renderizado dentro de um elemento com white-space: pre-wrap.
 */
export function formatWhatsAppText(text: string, renderPhone?: PhoneRenderer): ReactNode {
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <Fragment key={i}>
      {i > 0 && '\n'}
      {parseLine(line, renderPhone)}
    </Fragment>
  ));
}
